/**
 * THE EKUBA PROJECT — centralized site configuration.
 *
 * Everything that is content / external destination lives here.
 * ── URLs left as empty strings are NOT configured yet: the control keeps its exact
 *    visual appearance, and clicking it shows a short "not configured" notice
 *    instead of navigating to a fabricated destination.
 * ── `audioUrl` empty = no legitimate audio source supplied → play/pause will not
 *    pretend to play; it shows a notice instead.
 */

export type NavId = 'home' | 'music' | 'videos' | 'releases' | 'about' | 'contact';

export interface NavItem {
  id: NavId;
  label: string;
  target: string; // DOM id of the section to scroll to
  x: number; // desktop design x (px @1024)
}

export interface Release {
  id: string;
  title: string;
  artist: string;
  type: string;
  year: string;
  cover: string;
  url: string;
  /** desktop geometry measured from the reference */
  x: number;
  w: number;
}

export interface Track {
  id: string;
  title: string;
  artist: string;
  thumb: string;
  duration: number; // seconds (as displayed in the reference)
  initialPosition: number; // seconds (reference shows Asere at 2:18)
  audioUrl: string;
  url: string;
}

export interface Platform {
  id: string;
  label: string;
  logo: string;
  url: string;
  /** button box + logo box measured from the reference (design px) */
  x: number;
  w: number;
  logoX: number;
  logoY: number;
  logoW: number;
  logoH: number;
}

export type SocialId = 'instagram' | 'facebook' | 'youtube' | 'soundcloud' | 'x';

export interface Social {
  id: SocialId;
  label: string;
  url: string;
}

export const SITE = {
  name: 'The Ekuba Project',
  tagline: 'Music Has No Borders',
  /** "Listen Now" destination. Empty → scrolls to Featured Tracks. */
  listenNowUrl: '',
  /** "Listen on Spotify" link above featured tracks. */
  spotifyArtistUrl: '',
  /** "View all releases" destination. Empty → scrolls to Latest Releases. */
  allReleasesUrl: '',
  /** Contact e-mail used by the "Contact" nav item (mailto). Empty → scrolls to footer. */
  contactEmail: '',
  copyright: '© 2024 The Ekuba Project. All rights reserved.',
};

export const NAV: NavItem[] = [
  { id: 'home', label: 'Home', target: 'home', x: 345 },
  { id: 'music', label: 'Music', target: 'music', x: 413 },
  { id: 'videos', label: 'Videos', target: 'videos', x: 481 },
  { id: 'releases', label: 'Releases', target: 'releases', x: 555 },
  { id: 'about', label: 'About', target: 'about', x: 641 },
  { id: 'contact', label: 'Contact', target: 'contact', x: 713 },
];

export const RELEASES: Release[] = [
  { id: 'midnight-asphalt', title: 'Midnight Asphalt', artist: 'The Ekuba Project', type: 'Album', year: '2024', cover: '/assets/releases/midnight-asphalt.jpg', url: '', x: 44, w: 183 },
  { id: 'ekubastic', title: 'Ekubastic', artist: 'The Ekuba Project', type: 'Album', year: '2023', cover: '/assets/releases/ekubastic.jpg', url: '', x: 240, w: 177 },
  { id: 'same-old-bullshit', title: 'Same Old Bullshit', artist: 'The Ekuba Project', type: 'Single', year: '2023', cover: '/assets/releases/same-old-bullshit.jpg', url: '', x: 427, w: 174 },
  { id: 'back-to-basics', title: 'Back To Basics EP', artist: 'The Ekuba Project', type: 'EP', year: '2022', cover: '/assets/releases/back-to-basics.jpg', url: '', x: 611, w: 185 },
  { id: 'a-few-words', title: 'A Few Words', artist: 'The Ekuba Project', type: 'Single', year: '2021', cover: '/assets/releases/a-few-words.jpg', url: '', x: 807, w: 182 },
];

export const TRACKS: Track[] = [
  { id: 'asere', title: 'Asere', artist: 'The Ekuba Project', thumb: '/assets/tracks/asere.jpg', duration: 384, initialPosition: 138, audioUrl: '', url: '' },
  { id: 'electronic-inspiration', title: 'Electronic Inspiration', artist: 'The Ekuba Project', thumb: '/assets/tracks/electronic-inspiration.jpg', duration: 432, initialPosition: 0, audioUrl: '', url: '' },
  { id: 'french-kiss', title: 'French Kiss', artist: 'The Ekuba Project', thumb: '/assets/tracks/french-kiss.jpg', duration: 368, initialPosition: 0, audioUrl: '', url: '' },
];

export const PLATFORMS: Platform[] = [
  { id: 'spotify', label: 'Spotify', logo: '/assets/brand/spotify.png', url: '', x: 46, w: 127, logoX: 67, logoY: 1340, logoW: 84, logoH: 29 },
  { id: 'apple-music', label: 'Apple Music', logo: '/assets/brand/apple-music.png', url: '', x: 182, w: 122, logoX: 198, logoY: 1343, logoW: 89, logoH: 23 },
  { id: 'amazon-music', label: 'Amazon Music', logo: '/assets/brand/amazon-music.png', url: '', x: 313, w: 123, logoX: 328, logoY: 1345, logoW: 94, logoH: 23 },
  { id: 'soundcloud', label: 'SoundCloud', logo: '/assets/brand/soundcloud.png', url: '', x: 446, w: 125, logoX: 458, logoY: 1344, logoW: 102, logoH: 20 },
  { id: 'bandcamp', label: 'Bandcamp', logo: '/assets/brand/bandcamp.png', url: '', x: 580, w: 134, logoX: 597, logoY: 1347, logoW: 99, logoH: 20 },
];

/** Header social icons (reference shows 4) */
export const HEADER_SOCIALS: SocialId[] = ['instagram', 'facebook', 'youtube', 'soundcloud'];
/** "Follow the journey" icons (reference shows 5) */
export const FOLLOW_SOCIALS: SocialId[] = ['instagram', 'facebook', 'youtube', 'soundcloud', 'x'];

export const SOCIALS: Record<SocialId, Social> = {
  instagram: { id: 'instagram', label: 'Instagram', url: '' },
  facebook: { id: 'facebook', label: 'Facebook', url: '' },
  youtube: { id: 'youtube', label: 'YouTube', url: '' },
  soundcloud: { id: 'soundcloud', label: 'SoundCloud', url: '' },
  x: { id: 'x', label: 'X', url: '' },
};

export const ABOUT_FEATURES = [
  { id: 'electronic-roots', icon: 'globe', lines: ['ELECTRONIC', 'ROOTS'] },
  { id: 'latin-influences', icon: 'palm', lines: ['LATIN', 'INFLUENCES'] },
  { id: 'global-perspective', icon: 'wave', lines: ['GLOBAL', 'PERSPECTIVE'] },
] as const;
